<template>
  <div
    :id="calculatedId"
    :class="[
      bem({
        aspectRatio: props.aspectRatio,
        desktopLayout: props.desktopLayout,
        mobileLayout: props.mobileLayout,
        isFirstOnPage: props.rowPosition === 0
      }),
      'swiper',
    ]"
  >
    <div :class="[bem('items'), 'swiper-wrapper']">
      <div
        v-for="(item, index) in props.items"
        :key="'MediaHero-' + index"
        :class="[bem('item'), 'swiper-slide']"
      >
        <DatoCmsImage
          v-if="item.image"
          :data="item.image.responsiveImage"
          object-fit="cover"
          :object-position="`${item.image.focalPoint.x * 100}% ${
            item.image.focalPoint.y * 100
          }%`"
          layout="fill"
          :priority="rowPosition === 0"
        />
        <template v-else>
          <BaseVideo
            :video="item.video"
            :preview-start-time="0"
            :will-loop="true"
            :bg-video="true"
            :full-width="false"
          />
        </template>
        <div :class="bem('item-content-wrapper')">
          <div
            v-if="item.title || item.subtitle"
            :class="bem('item-content')"
          >
            <VisionHeading
              v-if="item.title"
              tag="h1"
              size="h1"
              :class="bem('title')"
            >
              {{ item.title }}
            </VisionHeading>
            <p
              v-if="item.subtitle"
              :class="bem('subtitle')"
            >
              {{ item.subtitle }}
            </p>
            <div v-if="item.buttons?.length" :class="bem('buttons')">
              <VisionButton
                v-for="(button, buttonIndex) in item.buttons"
                :key="buttonIndex"
                size="x-small"
                :to="button.url"
                :outline="button.style === 'outline'"
                :background-colour="button.colour || 'white'"
                :spa-link="button.url.includes('?modal=')"
                @click="handleLinkClick(`Item ${index} - Button ${buttonIndex}`)"
              >
                <template #content>
                  {{ button.text }}
                </template>
              </VisionButton>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div :id="calculatedId + '-pagination'" :class="[bem('pagination'), 'swiper-pagination']" />
  </div>
</template>

<script setup lang="ts">
import { Image as DatoCmsImage } from 'vue-datocms'
import Swiper from 'swiper'
import { Autoplay, Pagination } from 'swiper/modules'

interface MediaHeroItem {
  title?: string
  subtitle?: string
  image?: any
  video?: any
  buttons?: {
    text: string
    url: string
    colour: 'white' | 'black'
    style: 'filled' | 'outline'
  }[]
}

interface MediaHeroProps {
  id?: string
  items: MediaHeroItem[]
  aspectRatio: '2-3' | '4-5' | '1-1'
  rowPosition: number
  desktopLayout: 'carousel' | 'static'
  mobileLayout: 'carousel' | 'static'
}

const props = defineProps<MediaHeroProps>()

const bem = useBEM('MediaHero')
const { isMdBreakpointOrAbove } = useBreakpoint()
const { $track } = useNuxtApp()

const swiperInstance = ref()

const calculatedId = computed(() => {
  return `${props.id}-media-hero`
})

function createSwiperInstance() {
  return new Swiper(`#${calculatedId.value}`, {
    modules: [Autoplay, Pagination],
    autoplay: {
      delay: 15000,
      disableOnInteraction: false
    },
    slidesPerView: 1,
    breakpoints: {
      1000: {
        slidesPerView: 2,
      },
    },
    pagination: {
      el: `#${calculatedId.value}-pagination`,
      bulletActiveClass: 'MediaHero__pagination-bullet--active',
      bulletClass: 'MediaHero__pagination-bullet',
    },
  })
}

async function handleLinkClick(elementId: string) {
  await $track.global.linkClicked({
    interactionElement: elementId,
    interactionCategory: 'Media Hero',
    position: props.rowPosition + 1,
  }, 'Clicked Media Hero ' + elementId)
}

watch(isMdBreakpointOrAbove, (newValue) => {
  if(newValue && props.desktopLayout === 'static') {
    swiperInstance.value.destroy()
  } else {
    if(swiperInstance.value?.destroyed === true) {
      swiperInstance.value = createSwiperInstance()
    }
  }
})

onMounted(() => {
  if (props.desktopLayout === 'static' && props.mobileLayout === 'static') {
    return
  } else if (props.mobileLayout === 'carousel' && !isMdBreakpointOrAbove.value) {
    swiperInstance.value = createSwiperInstance()
    return
  }

  swiperInstance.value = createSwiperInstance()
})
</script>

<style lang="postcss">
.MediaHero {
  @screen sm {
    @apply mb-6;
  }

  &.swiper {
    @apply sm:overflow-visible;
  }
}

.MediaHero--is-first-on-page {
  .MediaHero__item-content-wrapper {
    max-height: 100%;
    height: calc(100vh - var(--nav-height) - var(--promo-bar-height));
    height: calc(100dvh - var(--nav-height) - var(--promo-bar-height));
    top: calc(var(--nav-height) + var(--promo-bar-height));
  }
}

.MediaHero--mobile-layout-carousel {
  .MediaHero__items {
    @screen lgMax {
      @apply flex-row;
    }
  }
}

.MediaHero--desktop-layout-static {
  .MediaHero__pagination {
    @screen lg {
      @apply hidden;
    }
  }
}

.MediaHero--mobile-layout-static {
  .MediaHero__items {
    @screen lgMax {
      @apply flex-col;
    }
  }
}

.MediaHero__items {
  @apply flex flex-col w-full;

  /**
    Offset the height of the sticky pagination items to avoid
    white space at bottom of carousel.
   */
  @screen sm {
    @apply -mb-6;
  }

  @screen lg {
    @apply flex-row;
  }
}

.MediaHero__item {
  @apply relative w-full;

  @screen lg {
    @apply w-1/2 flex-shrink-0;
  }
}

.MediaHero__item-content-wrapper {
  @apply sticky top-0 max-h-full z-[1];

  height: 100vh;
  height: 100dvh;
}

.MediaHero__item-content {
  @apply absolute bottom-0 w-full text-white px-4 pb-8;

  @screen sm {
    @apply pb-20 pr-6;

    padding-left: var(--outer-gutter);
  }
}

.MediaHero__title {
  @apply mb-2;

  @screen lg {
    --vision-heading-size: 52px;
  }

  @screen xl {
    --vision-heading-size: 64px;
  }
}

.MediaHero__subtitle {
  @apply font-bold text-sm lg:text-base;
}

.MediaHero__buttons {
  @apply flex mt-5 gap-3;
}

/**

  PAGINATION

 */

.MediaHero__pagination {
  @apply w-full
    inline-flex gap-2 justify-end -translate-y-5 sm:-translate-y-10
    pr-5
    absolute right-0 left-auto bottom-0;

  @screen sm {
    @apply sticky;
  }

  @screen lg {
    padding-left: var(--outer-gutter);
    padding-right: var(--outer-gutter);
  }
}

.MediaHero__pagination-bullet {
  @apply w-2 h-2 rounded-full bg-white opacity-25;
}

.MediaHero__pagination-bullet--active {
  @apply bg-white opacity-100;
}

/**

  MODIFIERS

 */

.MediaHero--aspect-ratio-2-3 {
  .MediaHero__item {
    aspect-ratio: 2/3;
  }

  @screen lgMax {
    .MediaHero__item-content {
      @apply pb-12;
    }
  }

  @screen smMax {
    .MediaHero__item-content {
      @apply pb-8;
    }
  }
}

.MediaHero--aspect-ratio-4-5 {
  .MediaHero__item {
    aspect-ratio: 4/5;
  }

  @screen lgMax {
    .MediaHero__item-content {
      @apply pb-12;
    }
  }

  @screen smMax {
    .MediaHero__item-content {
      @apply pb-8;
    }
  }
}

.MediaHero--aspect-ratio-1-1 {
  .MediaHero__item {
    aspect-ratio: 1/1;
  }
}
</style>
